import React, { useRef, useState, useEffect } from 'react';
import { FaPlus, FaMinus } from 'react-icons/fa6';
import Select, { MultiValue, SingleValue } from 'react-select';
import { fetchCampaignMetadata } from '~/services/campaign';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    useClearReportFilterParams,
    useReportFilters,
} from '~/context/ReportFiltersProvider';
import { ALL_AVAILABLE_COLUMNS, DEFAULT_VISIBLE_COLUMNS, isDefaultSelection, parseVisibleColumns } from './campaignApplicationColumns';
import ResetFiltersButton from './ResetFiltersButton';

export type AccordionItem = {
    id: number;
    active: number | null;
    handleToggle: (index: number) => void;
};

interface SelectOption {
    value: string;
    label: string;
}

const requirementsCompleteOptions: SelectOption[] = [
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
    { value: '4', label: '4' },
    { value: '5', label: '5' },
    { value: '6', label: '6' },
];

const prescreenResultOptions: SelectOption[] = [
    { value: 'call', label: 'Call' },
    { value: 'wait', label: 'Wait' },
    { value: 'reject', label: 'Reject' },
    { value: 'incomplete', label: 'Incomplete' },
];

// Date range presets for the Created date filter, mirroring the Campaign
// Request report so both reports offer the same "Last Month" etc. shortcuts.
interface DatePreset {
    value: string;
    label: string;
    getRange: () => { start: string; end: string };
}

const formatDate = (date: Date): string => {
    return date.toISOString().split('T')[0];
};

const datePresetOptions: SelectOption[] = [
    { value: '', label: 'Select date range' },
    { value: 'last_7_days', label: 'Last 7 Days' },
    { value: 'last_30_days', label: 'Last 30 Days' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'this_quarter', label: 'This Quarter' },
    { value: 'last_quarter', label: 'Last Quarter' },
    { value: 'this_year', label: 'This Year' },
    { value: 'last_year', label: 'Last Year' },
    { value: 'current_fiscal_year', label: 'Current Fiscal Year' },
];

const getDatePresets = (): DatePreset[] => {
    const today = new Date();

    return [
        {
            value: '',
            label: 'Select date range',
            getRange: () => ({ start: '', end: '' }),
        },
        {
            value: 'last_7_days',
            label: 'Last 7 Days',
            getRange: () => {
                const end = new Date(today);
                const start = new Date(today);
                start.setDate(start.getDate() - 7);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'last_30_days',
            label: 'Last 30 Days',
            getRange: () => {
                const end = new Date(today);
                const start = new Date(today);
                start.setDate(start.getDate() - 30);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'this_month',
            label: 'This Month',
            getRange: () => {
                const start = new Date(
                    today.getFullYear(),
                    today.getMonth(),
                    1,
                );
                const end = new Date(today);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'last_month',
            label: 'Last Month',
            getRange: () => {
                const start = new Date(
                    today.getFullYear(),
                    today.getMonth() - 1,
                    1,
                );
                const end = new Date(today.getFullYear(), today.getMonth(), 0); // Last day of previous month
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'this_quarter',
            label: 'This Quarter',
            getRange: () => {
                const currentQuarter = Math.floor(today.getMonth() / 3);
                const start = new Date(
                    today.getFullYear(),
                    currentQuarter * 3,
                    1,
                );
                const end = new Date(today);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'last_quarter',
            label: 'Last Quarter',
            getRange: () => {
                const currentQuarter = Math.floor(today.getMonth() / 3);
                const lastQuarter = currentQuarter - 1;
                const year =
                    lastQuarter < 0
                        ? today.getFullYear() - 1
                        : today.getFullYear();
                const quarter = lastQuarter < 0 ? 3 : lastQuarter;
                const start = new Date(year, quarter * 3, 1);
                const end = new Date(year, quarter * 3 + 3, 0); // Last day of the quarter
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'this_year',
            label: 'This Year',
            getRange: () => {
                const start = new Date(today.getFullYear(), 0, 1);
                const end = new Date(today);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'last_year',
            label: 'Last Year',
            getRange: () => {
                const start = new Date(today.getFullYear() - 1, 0, 1);
                const end = new Date(today.getFullYear() - 1, 11, 31);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
        {
            value: 'current_fiscal_year',
            label: 'Current Fiscal Year',
            getRange: () => {
                const fyStartYear =
                    today.getMonth() >= 9
                        ? today.getFullYear()
                        : today.getFullYear() - 1;
                const start = new Date(fyStartYear, 9, 1);
                const end = new Date(today);
                return { start: formatDate(start), end: formatDate(end) };
            },
        },
    ];
};

const selectClassNames = {
    control: () =>
        'w-full rounded-[4px] border border-[#949599] text-[16px]/[20px]',
    placeholder: () => 'text-[#949599]',
    multiValue: () => 'bg-[#F4F4F5] rounded-[4px] px-2 py-1 mr-1 mb-1',
    multiValueLabel: () => 'text-[14px]',
    multiValueRemove: () =>
        'hover:bg-[#F02D3A] hover:text-white rounded-r-[4px] px-1',
};

const EMPTY_FORM_VALUES = {
    contact_name: '',
    client_name: '',
    created_by: '',
    photo_consented: '',
    created_start_date: '',
    created_end_date: '',
};

const CampaignApplicationAccordionItem: React.FC<AccordionItem> = ({
    active,
    handleToggle,
    id,
}) => {
    const contentEl = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { setFilterParams, setActiveAccordion } = useReportFilters();
    const clearReportFilterParams = useClearReportFilterParams();

    const [communityOptions, setCommunityOptions] = useState<SelectOption[]>(
        [],
    );
    const [insuranceOptions, setInsuranceOptions] = useState<SelectOption[]>(
        [],
    );
    const [cscOptions, setCscOptions] = useState<SelectOption[]>([]);
    const [relationshipOptions, setRelationshipOptions] = useState<
        SelectOption[]
    >([]);
    const [referralSourceOptions, setReferralSourceOptions] = useState<
        SelectOption[]
    >([]);
    const [isLoading, setIsLoading] = useState(false);

    const [selectedCommunities, setSelectedCommunities] = useState<
        SelectOption[]
    >([]);
    const [selectedInsurances, setSelectedInsurances] = useState<
        SelectOption[]
    >([]);
    const [selectedRelationships, setSelectedRelationships] = useState<
        SelectOption[]
    >([]);
    const [selectedReferralSources, setSelectedReferralSources] = useState<
        SelectOption[]
    >([]);
    const [selectedCsc, setSelectedCsc] =
        useState<SingleValue<SelectOption>>(null);
    const [selectedRequirementsComplete, setSelectedRequirementsComplete] =
        useState<SelectOption[]>([]);
    const [selectedPrescreenResult, setSelectedPrescreenResult] =
        useState<SingleValue<SelectOption>>(null);
    const [selectedReferredBy, setSelectedReferredBy] =
        useState<SingleValue<SelectOption>>(null);

    const [selectedCreatedDatePreset, setSelectedCreatedDatePreset] =
        useState('');
    const datePresets = getDatePresets();

    const [formValues, setFormValues] = useState(EMPTY_FORM_VALUES);

    // Column picker: seed from the URL (Back-to-Search round-trip), else defaults.
    const [showColumns, setShowColumns] = useState(false);
    const [selectedColumns, setSelectedColumns] = useState<string[]>(
        () => parseVisibleColumns(searchParams.get('visible_columns')),
    );

    const toggleColumn = (key: string) => {
        setSelectedColumns(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key],
        );
    };

    useEffect(() => {
        const loadMetadata = async () => {
            setIsLoading(true);
            try {
                const metadata = await fetchCampaignMetadata();

                if (metadata?.community_types) {
                    setCommunityOptions(
                        metadata.community_types.map((ct: any) => ({
                            value: String(ct.id),
                            label: ct.name,
                        })),
                    );
                }

                if (metadata?.insurance_types) {
                    setInsuranceOptions(
                        metadata.insurance_types.map((it: any) => ({
                            value: String(it.id),
                            label: it.name,
                        })),
                    );
                }

                if (metadata?.patient_services_coordinators) {
                    setCscOptions(
                        metadata.patient_services_coordinators.map(
                            (psc: any) => ({
                                // The campaignApplications `assignee` filter matches
                                // on the CSC's Person id, so send person_id here (not
                                // the Role id used by CSC assignment).
                                value: String(psc.person_id),
                                label: psc.full_name,
                            }),
                        ),
                    );
                }

                if (metadata?.campaign_is_for) {
                    setRelationshipOptions(
                        metadata.campaign_is_for
                            .filter((cif: any) => cif.id)
                            .map((cif: any) => ({
                                value: String(cif.id),
                                label: cif.name,
                            })),
                    );
                }

                if (metadata?.heard_of_sources) {
                    setReferralSourceOptions(
                        metadata.heard_of_sources.map((src: any) => ({
                            value: String(src.id),
                            label: src.name,
                        })),
                    );
                }
            } catch (error) {
                console.error(
                    'Error fetching campaign application filter metadata:',
                    error,
                );
            } finally {
                setIsLoading(false);
            }
        };

        loadMetadata();
    }, []);

    useEffect(() => {
        if (searchParams.size === 0) return;

        setFormValues({
            contact_name: searchParams.get('contact_name') || '',
            client_name: searchParams.get('client_name') || '',
            created_by: searchParams.get('created_by') || '',
            photo_consented: searchParams.get('photo_consented') || '',
            created_start_date: searchParams.get('created_start_date') || '',
            created_end_date: searchParams.get('created_end_date') || '',
        });

        const reqCompleteParams = searchParams.getAll('requirements_complete');
        if (reqCompleteParams.length > 0) {
            setSelectedRequirementsComplete(
                requirementsCompleteOptions.filter((opt) =>
                    reqCompleteParams.includes(opt.value),
                ),
            );
        }

        const communityParams = searchParams.getAll('community');
        if (communityParams.length > 0 && communityOptions.length > 0) {
            setSelectedCommunities(
                communityOptions.filter((opt) =>
                    communityParams.includes(opt.value),
                ),
            );
        }

        const insuranceParams = searchParams.getAll('insurance');
        if (insuranceParams.length > 0 && insuranceOptions.length > 0) {
            setSelectedInsurances(
                insuranceOptions.filter((opt) =>
                    insuranceParams.includes(opt.value),
                ),
            );
        }

        const cscParam = searchParams.get('csc');
        if (cscParam && cscOptions.length > 0) {
            setSelectedCsc(
                cscOptions.find((opt) => opt.value === cscParam) || null,
            );
        }

        const relationshipParams = searchParams.getAll('pc_relationship');
        if (relationshipParams.length > 0 && relationshipOptions.length > 0) {
            setSelectedRelationships(
                relationshipOptions.filter((opt) =>
                    relationshipParams.includes(opt.value),
                ),
            );
        }

        const referralParams = searchParams.getAll('referral_source');
        if (referralParams.length > 0 && referralSourceOptions.length > 0) {
            setSelectedReferralSources(
                referralSourceOptions.filter((opt) =>
                    referralParams.includes(opt.value),
                ),
            );
        }

        const referredByParam = searchParams.get('referred_by');
        if (referredByParam && referralSourceOptions.length > 0) {
            setSelectedReferredBy(
                referralSourceOptions.find(
                    (opt) => opt.value === referredByParam,
                ) || null,
            );
        }

        const prescreenParam = searchParams.get('prescreen_result');
        if (prescreenParam) {
            setSelectedPrescreenResult(
                prescreenResultOptions.find(
                    (opt) => opt.value === prescreenParam,
                ) || null,
            );
        }
    }, [
        searchParams,
        communityOptions,
        insuranceOptions,
        cscOptions,
        relationshipOptions,
        referralSourceOptions,
    ]);

    const hasActiveFilters =
        Object.values(formValues).some((value) => value !== '') ||
        selectedCommunities.length > 0 ||
        selectedInsurances.length > 0 ||
        selectedRelationships.length > 0 ||
        selectedReferralSources.length > 0 ||
        selectedRequirementsComplete.length > 0 ||
        selectedCsc !== null ||
        selectedPrescreenResult !== null ||
        selectedReferredBy !== null;

    const handleResetFilters = () => {
        setFormValues(EMPTY_FORM_VALUES);
        setSelectedCommunities([]);
        setSelectedInsurances([]);
        setSelectedRelationships([]);
        setSelectedReferralSources([]);
        setSelectedRequirementsComplete([]);
        setSelectedCsc(null);
        setSelectedPrescreenResult(null);
        setSelectedReferredBy(null);
        setSelectedCreatedDatePreset('');
        clearReportFilterParams();
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        const params = new URLSearchParams();

        if (formValues.contact_name.trim()) {
            params.append('contact_name', formValues.contact_name.trim());
        }
        if (formValues.client_name.trim()) {
            params.append('client_name', formValues.client_name.trim());
        }

        selectedRequirementsComplete.forEach((opt) =>
            params.append('requirements_complete', opt.value),
        );
        selectedCommunities.forEach((opt) =>
            params.append('community', opt.value),
        );
        selectedInsurances.forEach((opt) =>
            params.append('insurance', opt.value),
        );

        if (selectedCsc) {
            params.append('csc', selectedCsc.value);
        }

        selectedRelationships.forEach((opt) =>
            params.append('pc_relationship', opt.value),
        );
        selectedReferralSources.forEach((opt) =>
            params.append('referral_source', opt.value),
        );

        if (selectedReferredBy) {
            params.append('referred_by', selectedReferredBy.value);
        }

        if (formValues.created_by) {
            params.append('created_by', formValues.created_by);
        }

        if (formValues.created_start_date) {
            params.append('created_start_date', formValues.created_start_date);
        }
        if (formValues.created_end_date) {
            params.append('created_end_date', formValues.created_end_date);
        }

        if (selectedPrescreenResult) {
            params.append('prescreen_result', selectedPrescreenResult.value);
        }

        if (formValues.photo_consented) {
            params.append('photo_consented', formValues.photo_consented);
        }

        // Append column selection only when it differs from the defaults,
        // keeping the common-case URL clean. Single comma-joined param to
        // match how the results page reads it.
        if (!isDefaultSelection(selectedColumns) && selectedColumns.length) {
            params.append('visible_columns', selectedColumns.join(','));
        }

        setFilterParams(params.toString());
        setActiveAccordion(2);
        navigate(`/reports/campaign-applications?${params.toString()}`);
    };

    return (
        <div
            className={`w-full max-w-[932px] rounded-sm border bg-white px-7.5 py-4 shadow-default ${
                active === id ? 'border-primary' : 'border-stroke'
            }`}
        >
            <button
                className={`flex w-full items-center justify-between gap-2 ${
                    active === id ? 'active' : ''
                }`}
                onClick={() => handleToggle(id)}
            >
                <div>
                    <h4 className='text-title-xsm font-bold sm:text-title-md'>
                        Campaign Application
                    </h4>
                </div>
                <div className='flex items-center gap-2'>
                    <FaPlus className={`${active === id ? 'hidden' : ''}`} />
                    <FaMinus
                        className={`${active === id ? 'block' : 'hidden'}`}
                    />
                </div>
            </button>

            <div
                ref={contentEl}
                className={`py-5 duration-200 ease-in-out ${
                    active === id ? 'block' : 'hidden'
                }`}
            >
                <form onSubmit={handleSearch}>
                    <div className='w-full'>
                        <h2 className='text-[#949599] text-[18px/22px]'>
                            FILTERS
                        </h2>

                        <div className='mt-4'>
                            <label
                                htmlFor='ca-contact-name'
                                className='block text-[12px]/[14px] font-extrabold'
                            >
                                Contact Name
                            </label>
                            <input
                                type='text'
                                id='ca-contact-name'
                                placeholder='Contact Name'
                                value={formValues.contact_name}
                                onChange={(e) =>
                                    setFormValues((prev) => ({
                                        ...prev,
                                        contact_name: e.target.value,
                                    }))
                                }
                                className='w-full rounded-[4px] border border-[#949599] p-[10px] text-[16px]/[20px]'
                            />
                        </div>

                        <div className='mt-4'>
                            <label
                                htmlFor='ca-client-name'
                                className='block text-[12px]/[14px] font-extrabold'
                            >
                                Client Name
                            </label>
                            <input
                                type='text'
                                id='ca-client-name'
                                placeholder='Any'
                                value={formValues.client_name}
                                onChange={(e) =>
                                    setFormValues((prev) => ({
                                        ...prev,
                                        client_name: e.target.value,
                                    }))
                                }
                                className='w-full rounded-[4px] border border-[#949599] p-[10px] text-[16px]/[20px]'
                            />
                        </div>

                        <div className='mt-4'>
                            <label
                                htmlFor='ca-requirements'
                                className='block text-[12px]/[14px] font-extrabold'
                            >
                                Requirements Complete
                            </label>
                            <Select
                                id='ca-requirements'
                                menuPlacement='auto'
                                options={requirementsCompleteOptions}
                                isMulti
                                value={selectedRequirementsComplete}
                                onChange={(opts: MultiValue<SelectOption>) =>
                                    setSelectedRequirementsComplete([...opts])
                                }
                                placeholder='1, 2, 3, 4, 5, 6'
                                classNames={selectClassNames}
                            />
                        </div>

                        <div className='mt-4'>
                            <label
                                htmlFor='ca-community'
                                className='block text-[12px]/[14px] font-extrabold'
                            >
                                Community (select multiple)
                            </label>
                            <Select
                                id='ca-community'
                                menuPlacement='auto'
                                options={communityOptions}
                                isMulti
                                value={selectedCommunities}
                                onChange={(opts: MultiValue<SelectOption>) =>
                                    setSelectedCommunities([...opts])
                                }
                                isLoading={isLoading}
                                placeholder='Any'
                                classNames={selectClassNames}
                            />
                        </div>

                        <div className='mt-4'>
                            <label
                                htmlFor='ca-insurance'
                                className='block text-[12px]/[14px] font-extrabold'
                            >
                                Insurance (select multiple)
                            </label>
                            <Select
                                id='ca-insurance'
                                menuPlacement='auto'
                                options={insuranceOptions}
                                isMulti
                                value={selectedInsurances}
                                onChange={(opts: MultiValue<SelectOption>) =>
                                    setSelectedInsurances([...opts])
                                }
                                isLoading={isLoading}
                                placeholder='Any'
                                classNames={selectClassNames}
                            />
                        </div>

                        <div className='mt-4'>
                            <label
                                htmlFor='ca-csc'
                                className='block text-[12px]/[14px] font-extrabold'
                            >
                                CSC
                            </label>
                            <Select
                                id='ca-csc'
                                menuPlacement='auto'
                                options={cscOptions}
                                isClearable
                                value={selectedCsc}
                                onChange={(opt: SingleValue<SelectOption>) =>
                                    setSelectedCsc(opt)
                                }
                                isLoading={isLoading}
                                placeholder='Any CSC'
                                classNames={selectClassNames}
                            />
                        </div>

                        <div className='mt-4'>
                            <label
                                htmlFor='ca-relationship'
                                className='block text-[12px]/[14px] font-extrabold'
                            >
                                Primary contact relationship to client (select
                                multiple)
                            </label>
                            <Select
                                id='ca-relationship'
                                menuPlacement='auto'
                                options={relationshipOptions}
                                isMulti
                                value={selectedRelationships}
                                onChange={(opts: MultiValue<SelectOption>) =>
                                    setSelectedRelationships([...opts])
                                }
                                isLoading={isLoading}
                                placeholder='Any'
                                classNames={selectClassNames}
                            />
                        </div>

                        <div className='mt-4'>
                            <label
                                htmlFor='ca-referral-source'
                                className='block text-[12px]/[14px] font-extrabold'
                            >
                                Referral Source (select multiple)
                            </label>
                            <Select
                                id='ca-referral-source'
                                menuPlacement='auto'
                                options={referralSourceOptions}
                                isMulti
                                value={selectedReferralSources}
                                onChange={(opts: MultiValue<SelectOption>) =>
                                    setSelectedReferralSources([...opts])
                                }
                                isLoading={isLoading}
                                placeholder='Any'
                                classNames={selectClassNames}
                            />
                        </div>

                        <div className='mt-4'>
                            <label
                                htmlFor='ca-referred-by'
                                className='block text-[12px]/[14px] font-extrabold'
                            >
                                Referred by
                            </label>
                            <Select
                                id='ca-referred-by'
                                options={referralSourceOptions}
                                menuPlacement='auto'
                                isClearable
                                value={selectedReferredBy}
                                onChange={(opt: SingleValue<SelectOption>) =>
                                    setSelectedReferredBy(opt)
                                }
                                isLoading={isLoading}
                                placeholder='Start typing...'
                                classNames={selectClassNames}
                            />
                        </div>

                        <div className='mt-4'>
                            <label className='block text-[12px]/[14px] font-extrabold'>
                                Created date
                            </label>
                            <p className='text-[11px]/[14px] text-[#949599]'>
                                When the application was created (CR approved)
                            </p>
                            <div className='mt-1'>
                                <Select
                                    id='ca-created-preset'
                                    menuPlacement='auto'
                                    options={datePresetOptions}
                                    isClearable
                                    value={
                                        datePresetOptions.find(
                                            (opt) =>
                                                opt.value ===
                                                selectedCreatedDatePreset,
                                        ) || null
                                    }
                                    onChange={(opt: SingleValue<SelectOption>) => {
                                        const presetValue = opt?.value || '';
                                        setSelectedCreatedDatePreset(presetValue);
                                        const preset = datePresets.find(
                                            (p) => p.value === presetValue,
                                        );
                                        if (preset) {
                                            const range = preset.getRange();
                                            setFormValues((prev) => ({
                                                ...prev,
                                                created_start_date: range.start,
                                                created_end_date: range.end,
                                            }));
                                        }
                                    }}
                                    placeholder='Select date range'
                                    classNames={selectClassNames}
                                />
                            </div>
                            <div className='mt-1 flex items-center gap-3'>
                                <div className='flex-1'>
                                    <label
                                        htmlFor='ca-created-from'
                                        className='block text-[11px]/[14px] text-[#949599]'
                                    >
                                        From
                                    </label>
                                    <input
                                        type='date'
                                        id='ca-created-from'
                                        value={formValues.created_start_date}
                                        onChange={(e) => {
                                            setFormValues((prev) => ({
                                                ...prev,
                                                created_start_date:
                                                    e.target.value,
                                            }));
                                            setSelectedCreatedDatePreset(''); // Clear preset when manually editing
                                        }}
                                        className='w-full rounded-[4px] border border-[#949599] p-[10px] text-[16px]/[20px]'
                                    />
                                </div>
                                <div className='flex-1'>
                                    <label
                                        htmlFor='ca-created-to'
                                        className='block text-[11px]/[14px] text-[#949599]'
                                    >
                                        To
                                    </label>
                                    <input
                                        type='date'
                                        id='ca-created-to'
                                        value={formValues.created_end_date}
                                        min={
                                            formValues.created_start_date ||
                                            undefined
                                        }
                                        onChange={(e) => {
                                            setFormValues((prev) => ({
                                                ...prev,
                                                created_end_date:
                                                    e.target.value,
                                            }));
                                            setSelectedCreatedDatePreset(''); // Clear preset when manually editing
                                        }}
                                        className='w-full rounded-[4px] border border-[#949599] p-[10px] text-[16px]/[20px]'
                                    />
                                </div>
                            </div>
                        </div>

                        <div className='mt-4'>
                            <label className='block text-[12px]/[14px] font-extrabold'>
                                Created by
                            </label>
                            <div className='mt-1 flex items-center gap-4'>
                                <label className='flex cursor-pointer items-center gap-1.5 text-[14px]'>
                                    <input
                                        name='created_by'
                                        className='accent-[#00adb3]'
                                        type='radio'
                                        value=''
                                        checked={formValues.created_by === ''}
                                        onChange={(e) =>
                                            setFormValues((prev) => ({
                                                ...prev,
                                                created_by: e.target.value,
                                            }))
                                        }
                                    />
                                    Any
                                </label>
                                <label className='flex cursor-pointer items-center gap-1.5 text-[14px]'>
                                    <input
                                        name='created_by'
                                        className='accent-[#00adb3]'
                                        type='radio'
                                        value='csc'
                                        checked={
                                            formValues.created_by === 'csc'
                                        }
                                        onChange={(e) =>
                                            setFormValues((prev) => ({
                                                ...prev,
                                                created_by: e.target.value,
                                            }))
                                        }
                                    />
                                    CSC
                                </label>
                                <label className='flex cursor-pointer items-center gap-1.5 text-[14px]'>
                                    <input
                                        name='created_by'
                                        className='accent-[#00adb3]'
                                        type='radio'
                                        value='web'
                                        checked={
                                            formValues.created_by === 'web'
                                        }
                                        onChange={(e) =>
                                            setFormValues((prev) => ({
                                                ...prev,
                                                created_by: e.target.value,
                                            }))
                                        }
                                    />
                                    Web
                                </label>
                            </div>
                        </div>

                        <div className='mt-4'>
                            <label
                                htmlFor='ca-prescreen'
                                className='block text-[12px]/[14px] font-extrabold'
                            >
                                Prescreen result
                            </label>
                            <Select
                                id='ca-prescreen'
                                menuPlacement='auto'
                                options={prescreenResultOptions}
                                isClearable
                                value={selectedPrescreenResult}
                                onChange={(opt: SingleValue<SelectOption>) =>
                                    setSelectedPrescreenResult(opt)
                                }
                                placeholder='Any result'
                                classNames={selectClassNames}
                            />
                        </div>

                        <div className='mt-4'>
                            <label className='block text-[12px]/[14px] font-extrabold'>
                                Photo Consented
                            </label>
                            <div className='mt-1 flex items-center gap-4'>
                                <label className='flex cursor-pointer items-center gap-1.5 text-[14px]'>
                                    <input
                                        name='photo_consented'
                                        className='accent-[#00adb3]'
                                        type='radio'
                                        value='true'
                                        checked={
                                            formValues.photo_consented ===
                                            'true'
                                        }
                                        onChange={(e) =>
                                            setFormValues((prev) => ({
                                                ...prev,
                                                photo_consented: e.target.value,
                                            }))
                                        }
                                    />
                                    True
                                </label>
                                <label className='flex cursor-pointer items-center gap-1.5 text-[14px]'>
                                    <input
                                        name='photo_consented'
                                        className='accent-[#00adb3]'
                                        type='radio'
                                        value='false'
                                        checked={
                                            formValues.photo_consented ===
                                            'false'
                                        }
                                        onChange={(e) =>
                                            setFormValues((prev) => ({
                                                ...prev,
                                                photo_consented: e.target.value,
                                            }))
                                        }
                                    />
                                    False
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Column Configuration */}
                    <div className='mt-8 border-t border-gray-200 pt-6'>
                        <div className='flex items-center justify-between'>
                            <button
                                type='button'
                                onClick={() => setShowColumns((s) => !s)}
                                className='flex items-center gap-2 text-[12px] font-bold text-[#949599]'
                            >
                                COLUMNS ({selectedColumns.length} selected)
                                {showColumns ? (
                                    <FaMinus size={10} />
                                ) : (
                                    <FaPlus size={10} />
                                )}
                            </button>
                            {!isDefaultSelection(selectedColumns) && (
                                <button
                                    type='button'
                                    onClick={() =>
                                        setSelectedColumns(DEFAULT_VISIBLE_COLUMNS)
                                    }
                                    className='text-[12px] text-gray-500 underline hover:text-gray-700'
                                >
                                    Reset to default
                                </button>
                            )}
                        </div>
                        {showColumns && (
                            <div className='mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4'>
                                {ALL_AVAILABLE_COLUMNS.map((column) => (
                                    <label
                                        key={column.key}
                                        className='flex cursor-pointer items-center gap-2 text-sm'
                                    >
                                        <input
                                            type='checkbox'
                                            checked={selectedColumns.includes(
                                                column.key,
                                            )}
                                            onChange={() =>
                                                toggleColumn(column.key)
                                            }
                                            className='rounded border-gray-300 text-primary focus:ring-primary'
                                        />
                                        <span className='text-gray-700'>
                                            {column.label}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className='mt-8 flex flex-wrap justify-center gap-4'>
                        <ResetFiltersButton
                            onReset={handleResetFilters}
                            disabled={!hasActiveFilters}
                        />
                        <button
                            type='submit'
                            className='rounded-lg bg-primary px-8 py-3 text-center text-sm font-medium text-white hover:bg-primary/90 focus:outline-none focus:ring-4 focus:ring-primary/50'
                        >
                            Search Campaign Applications
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CampaignApplicationAccordionItem;
